const renameWithExtension = (name: string, nextExtension: string) => {
  const base = name.replace(/\.[^.]+$/, "");
  return `${base}.${nextExtension}`;
};

const extensionForMimeType = (mimeType: string, fallbackName: string) => {
  switch (mimeType) {
    case "image/webp":
      return "webp";
    case "image/png":
      return "png";
    case "image/jpeg":
      return "jpg";
    default:
      return fallbackName.split(".").pop() || "bin";
  }
};

const canvasToBlob = (canvas: HTMLCanvasElement, type: string, quality?: number) =>
  new Promise<Blob | null>((resolve) => {
    canvas.toBlob((value) => resolve(value), type, quality);
  });

type OptimizeImageOptions = {
  maxDimension?: number;
  minBytes?: number;
};

export const optimizeImageForUpload = async (
  input: File,
  options?: OptimizeImageOptions,
): Promise<File> => {
  if (!input.type.startsWith("image/")) return input;
  if (input.type === "image/gif") return input;

  const minBytes = options?.minBytes ?? 300 * 1024;
  if (input.size < minBytes) return input;

  const sourceUrl = URL.createObjectURL(input);
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error("Image decode failed"));
      img.src = sourceUrl;
    });

    const maxDimension = options?.maxDimension ?? 2200;
    const scale = Math.min(1, maxDimension / Math.max(image.width, image.height));
    const width = Math.max(1, Math.round(image.width * scale));
    const height = Math.max(1, Math.round(image.height * scale));

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext("2d");
    if (!context) return input;
    context.drawImage(image, 0, 0, width, height);

    const candidates =
      input.type === "image/png"
        ? ["image/webp", "image/png"]
        : input.type === "image/jpeg" || input.type === "image/webp"
          ? [input.type, "image/jpeg", "image/webp"]
          : ["image/jpeg", "image/webp", "image/png"];

    let bestBlob: Blob | null = null;
    let bestType = input.type;

    for (const candidateType of candidates) {
      const quality = candidateType === "image/png" ? undefined : 0.92;
      const blob = await canvasToBlob(canvas, candidateType, quality);
      if (!blob) continue;
      if (!bestBlob || blob.size < bestBlob.size) {
        bestBlob = blob;
        bestType = candidateType;
      }
    }

    if (!bestBlob || bestBlob.size >= input.size) return input;

    const extension = extensionForMimeType(bestType, input.name);
    return new File([bestBlob], renameWithExtension(input.name, extension), {
      type: bestType,
      lastModified: Date.now(),
    });
  } catch {
    return input;
  } finally {
    URL.revokeObjectURL(sourceUrl);
  }
};

